/**
 * E2E Testing Helpers
 * 
 * Utilities for setting up editors with test attributes and API access.
 * Only active when running in Cypress environment.
 * 
 * Based on best practices:
 * - 90% API-based testing (fast, reliable)
 * - 10% UI-based testing (critical user paths)
 */

/**
 * Sets up Monaco Editor for E2E testing
 * - Adds data-test attributes to internal elements
 * - Exposes editor API to window (Cypress only)
 * - Marks container as ready
 * 
 * @param {object} editor - Monaco editor instance
 * @param {React.RefObject} containerRef - Ref to container element
 * @param {string} testId - Optional unique identifier for this editor instance
 */
export const setupMonacoForTesting = (editor, containerRef, testId) => {
    if (!window.Cypress) return

    const domNode = editor.getDomNode()
    if (!domNode) return

    const suffix = testId ? `-${testId}` : ''

    // Add data-test attributes to Monaco's internal elements
    const selectors = {
        input: ['.native-edit-context', 'textarea.inputarea'], // Current + legacy
        content: ['.view-lines'],
        lineNumbers: ['.line-numbers']
    }

    Object.entries(selectors).forEach(([testName, classNames]) => {
        for (const className of classNames) {
            const element = domNode.querySelector(className)
            if (element) {
                element.setAttribute('data-test', `editor-${testName}${suffix}`)
                break
            }
        }
    })

    // Mark editor as ready
    if (containerRef.current) {
        containerRef.current.setAttribute('data-editor-ready', 'true')
    }

    // Expose editor API for testing (90% of tests use this)
    // If testId provided, use it as key; otherwise use default
    const apiKey = testId ? `monacoEditorAPI_${testId}` : 'monacoEditorAPI'
    const instanceKey = testId ? `monacoEditor_${testId}` : 'monacoEditor'
    
    window[instanceKey] = editor
    window[apiKey] = {
        setValue: (content) => editor.setValue(content),
        getValue: () => editor.getValue(),
        clear: () => editor.setValue(''),
        focus: () => editor.focus(),
        getLineCount: () => editor.getModel()?.getLineCount() || 0,
        isReady: () => !!editor.getModel()
    }
}

/**
 * Sets up Quill Editor (TextEditor) for E2E testing
 * - Adds data-test attributes
 * - Exposes editor API to window (Cypress only)
 * - Marks container as ready
 * 
 * @param {object} quillRef - React ref to ReactQuill component
 * @param {React.RefObject} containerRef - Ref to container element
 * @param {string} testId - Optional unique identifier for this editor instance
 */
export const setupQuillForTesting = (quillRef, containerRef, testId) => {
    if (!window.Cypress) return
    if (!quillRef.current) return

    const quill = quillRef.current.getEditor()
    if (!quill) return

    const suffix = testId ? `-${testId}` : ''

    const container = quill.container
    if (container) {
        // Add data-test attributes to Quill's internal elements
        const editor = container.querySelector('.ql-editor')
        if (editor) {
            editor.setAttribute('data-test', `quill-editor${suffix}`)
        }

        const toolbar = container.parentElement?.querySelector('.ql-toolbar')
        if (toolbar) {
            toolbar.setAttribute('data-test', `quill-toolbar${suffix}`)
        }
    }

    // Mark container as ready
    if (containerRef.current) {
        containerRef.current.setAttribute('data-editor-ready', 'true')
    }

    // Expose Quill API for testing (90% of tests use this)
    // If testId provided, use it as key; otherwise use default
    const apiKey = testId ? `quillEditorAPI_${testId}` : 'quillEditorAPI'
    const instanceKey = testId ? `quillEditor_${testId}` : 'quillEditor'
    
    window[instanceKey] = quill
    window[apiKey] = {
        setText: (text) => quill.setText(text),
        setContents: (delta) => quill.setContents(delta),
        getText: () => quill.getText(),
        getHTML: () => quill.root.innerHTML,
        getLength: () => quill.getLength(),
        clear: () => quill.setText(''),
        focus: () => quill.focus(),
        isReady: () => !!quill.root
    }
}
